"use client";

import { articles } from "@/data/articles";
import { articleContent } from "@/data/article-content";
import { GuideArticleCard } from "@/app/guides/_components/guide-article-card";
import { restoreGuidesScrollPosition } from "@/app/guides/_components/guides-scroll";
import {
  GuidesSidebarFilters,
  type GuideSort,
} from "@/app/guides/_components/guides-sidebar-filters";
import { GuidesSearchBar } from "@/app/guides/_components/guides-search-bar";
import { HomeSectionScroll } from "@/components/sections/home-section-scroll";
import { GUIDE_CATEGORY_FILTERS, HERO_GRADIENT_TEXT } from "@/lib/light-pages";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;

function entranceTransition(
  reducedMotion: boolean | null,
  delay = 0,
  duration = 0.55,
) {
  if (reducedMotion) return { duration: 0 };
  return { delay, duration, ease: SEQUENCE_EASE };
}

const normalize = (str: string): string =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, "");

const similarity = (str1: string, str2: string): number => {
  const s1 = normalize(str1);
  const s2 = normalize(str2);
  if (s1 === s2) return 1;
  if (s1.includes(s2) || s2.includes(s1)) {
    return (Math.max(s1.length, s2.length) / Math.min(s1.length, s2.length)) * 0.8;
  }

  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  if (longer.length === 0) return 1;

  let matches = 0;
  let j = 0;
  for (let i = 0; i < shorter.length && j < longer.length; i++) {
    if (shorter[i] === longer[j]) {
      matches++;
      j++;
    } else {
      const found = longer.indexOf(shorter[i], j);
      if (found !== -1) {
        matches++;
        j = found + 1;
      }
    }
  }

  const similarityScore = matches / longer.length;
  return similarityScore >= 0.7 ? similarityScore : 0;
};

const fuzzyMatch = (query: string, text: string, threshold = 0.6): boolean => {
  if (!query.trim()) return true;

  const normalizedQuery = normalize(query);
  const normalizedText = normalize(text);
  if (normalizedText.includes(normalizedQuery)) return true;

  const queryWords = normalizedQuery.split(/\s+/).filter((w) => w.length > 2);
  if (queryWords.length > 0) {
    const allWordsMatch = queryWords.every((word) => {
      if (normalizedText.includes(word)) return true;
      const words = normalizedText.split(/\s+/);
      return words.some((textWord) => similarity(word, textWord) >= threshold);
    });
    if (allWordsMatch) return true;
  }

  return similarity(normalizedQuery, normalizedText) >= threshold;
};

function sortArticles(items: typeof articles, sort: GuideSort) {
  return [...items].sort((a, b) => {
    const dateA = new Date(a.publishedAt).getTime();
    const dateB = new Date(b.publishedAt).getTime();
    return sort === "recent" ? dateB - dateA : dateA - dateB;
  });
}

export default function GuidesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("Tous");
  const [sort, setSort] = useState<GuideSort>("recent");
  const sectionRef = useRef<HTMLElement>(null);
  const articlesScrollRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.08 });
  const reducedMotion = useReducedMotion();

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { Tous: articles.length };
    for (const category of GUIDE_CATEGORY_FILTERS) {
      if (category === "Tous") continue;
      counts[category] = articles.filter((article) => article.category === category).length;
    }
    return counts;
  }, []);

  const filteredArticles = useMemo(() => {
    const matchesCategory = (article: (typeof articles)[number]) =>
      selectedCategory === "Tous" || article.category === selectedCategory;

    if (!searchQuery.trim()) {
      return sortArticles(
        articles.filter(matchesCategory),
        sort,
      );
    }

    const articlesWithScore = articles.map((article) => {
      let score = 0;
      const normalizedTitle = normalize(article.title);
      const normalizedQuery = normalize(searchQuery);

      if (normalizedTitle.includes(normalizedQuery)) {
        score = 100;
      } else if (fuzzyMatch(searchQuery, article.title, 0.7)) {
        score = 90;
      } else if (normalize(article.summary).includes(normalizedQuery)) {
        score = 50;
      } else if (fuzzyMatch(searchQuery, article.summary, 0.7)) {
        score = 40;
      } else if (normalize(article.category).includes(normalizedQuery)) {
        score = 35;
      } else if (
        searchQuery.trim().length > 4 &&
        articleContent[article.slug] &&
        fuzzyMatch(searchQuery, articleContent[article.slug], 0.75)
      ) {
        score = 20;
      }

      return { article, score };
    });

    const ranked = articlesWithScore
      .filter((item) => item.score > 0 && matchesCategory(item.article))
      .sort((a, b) => b.score - a.score)
      .map((item) => item.article);

    return sortArticles(ranked, sort);
  }, [searchQuery, selectedCategory, sort]);

  useEffect(() => {
    articlesScrollRef.current?.scrollTo({ top: 0 });
  }, [searchQuery, selectedCategory, sort]);

  useLayoutEffect(() => {
    restoreGuidesScrollPosition(articlesScrollRef.current);
  }, []);

  return (
    <div className="guides-page-light bg-[#f3f3f3] text-[#1C1C1C]">
      <HomeSectionScroll pageClass="guides-page-light" />
      <section
        ref={sectionRef}
        className="relative -mt-[calc(5rem+env(safe-area-inset-top))] flex h-[calc(100dvh-env(safe-area-inset-top))] flex-col overflow-hidden bg-[#f3f3f3] px-4 pb-4 pt-[calc(5rem+env(safe-area-inset-top)+0.75rem)] sm:px-6 sm:pb-5 sm:pt-[calc(5rem+env(safe-area-inset-top)+1rem)]"
      >
        <div className="relative mx-auto flex h-full min-h-0 w-full max-w-[88rem] flex-1 flex-col">
          <motion.div
            className="flex min-h-0 flex-1 flex-col rounded-[1.75rem] border border-zinc-200/90 bg-white shadow-[0_20px_60px_-28px_rgba(15,23,42,0.12)]"
            initial={false}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
            transition={entranceTransition(reducedMotion, 0.08, 0.55)}
          >
            <div className="shrink-0 overflow-hidden rounded-t-[1.75rem] border-b border-dashed border-zinc-200 bg-zinc-50/60 px-4 py-3 sm:px-6 sm:py-4 lg:px-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5 lg:gap-8">
                <motion.h1
                  className="shrink-0 text-left text-[1.35rem] font-bold leading-[1.08] tracking-[-0.04em] text-[#1C1C1C] sm:text-[1.65rem] lg:text-[1.75rem] lg:leading-[1.05]"
                  initial={false}
                  animate={
                    isInView
                      ? { opacity: 1, y: 0, filter: "blur(0px)" }
                      : { opacity: 0, y: 10, filter: "blur(8px)" }
                  }
                  transition={entranceTransition(reducedMotion, 0, 0.55)}
                >
                  Guides & <span className={HERO_GRADIENT_TEXT}>FAQ</span>
                </motion.h1>
                <GuidesSearchBar value={searchQuery} onChange={setSearchQuery} className="min-w-0 flex-1" />
              </div>
            </div>

            <div className="flex min-h-0 flex-1 overflow-hidden rounded-b-[1.75rem] lg:flex-row lg:gap-0">
              <aside className="shrink-0 border-b border-dashed border-zinc-200 px-4 py-4 sm:px-6 lg:w-70 lg:overflow-y-auto lg:border-b-0 lg:border-r lg:px-0 lg:py-5 lg:pl-6 lg:pr-5 xl:pl-8 xl:pr-6 [scrollbar-width:thin]">
                <GuidesSidebarFilters
                  sort={sort}
                  onSortChange={setSort}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  categoryCounts={categoryCounts}
                  resultCount={filteredArticles.length}
                  compact
                />
              </aside>

              <div className="flex min-h-0 min-w-0 flex-1 flex-col py-4 pl-4 pr-4 sm:py-5 sm:pl-5 sm:pr-5 lg:pl-6 xl:pl-8">
                {filteredArticles.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/80 px-6 py-10 text-center">
                    <div>
                      <p className="mb-2 text-lg font-bold text-[#1C1C1C]">Aucun guide trouvé</p>
                      <p className="text-sm text-zinc-500 sm:text-base">
                        Essayez un autre mot-clé ou revenez à la catégorie « Tous ».
                      </p>
                    </div>
                  </div>
                ) : (
                  <motion.div
                    ref={articlesScrollRef}
                    layoutRoot
                    className="guides-articles-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-12 pt-3 sm:px-3 sm:pb-14 [scrollbar-gutter:stable] [scrollbar-width:thin]"
                  >
                    <div className="mx-auto grid w-full max-w-xl grid-cols-1 gap-4 pb-8 sm:max-w-[54rem] sm:grid-cols-2 sm:gap-x-3 sm:gap-y-4 lg:max-w-[62rem]">
                      {filteredArticles.map((post) => (
                        <div key={post.slug} className="px-1 sm:px-1.5">
                          <GuideArticleCard article={post} compact />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
